import React, {useEffect, useState} from 'react';
import Util from '../forecast-app/shared/util/util';
import UpdateCustomFieldValueMutation from '../mutations/update_custom_field_value_mutation';
import {BasicNumberInput, BasicTextInput} from '@/web-components';
import {ComboBox, Text} from '@forecast-it/design-system';
import {CUSTOM_FIELD_TYPE, CustomFieldType, profileLocales} from '../constants';
import {DesignSystemComboBoxBorderAdapter} from '../forecast-app/shared/components/dropdowns/DesignSystemComboBoxOldLookAdapter';
type Option = {
	id: string;
	displayName: string;
};

type CustomFieldValueInputProps = {
	customFieldKey: unknown;
	displayName: unknown;
	value: string | null;
	entityType: unknown;
	entityId: unknown;
	type: CustomFieldType;
	options?: Option[];
	readOnly?: boolean;
	language?: string;
};

const GRAPHQL_TYPE_MAP: Record<CustomFieldType, string> = {
	[CUSTOM_FIELD_TYPE.STRING]: 'TextCustomFieldValue',
	[CUSTOM_FIELD_TYPE.DROPDOWN]: 'DropdownCustomFieldValue',
	[CUSTOM_FIELD_TYPE.INTEGER]: 'IntegerCustomFieldValue',
	[CUSTOM_FIELD_TYPE.DECIMAL]: 'DecimalCustomFieldValue',
};

const isValidInteger = (val: string | null): boolean => {
	if (val === null || val === '') return true;
	return /^-?\d+$/.test(val);
};

const integerConverterValue = (value: string | number | null) => {
	if (value === null || value === undefined || value === '') {
		return {float: null, full: '', error: false};
	}
	const str = String(value).trim();
	if (/^-?\d+$/.test(str)) {
		return {float: parseInt(str, 10), full: str, error: false};
	}
	return {float: null, full: str, error: false};
};

const filterIntegerInput = (val: string): string => {
	const stripped = val.replace(/[^0-9-]/g, '');
	if (stripped === '-') return '-';
	const negative = stripped.startsWith('-');
	const digits = stripped.replace(/-/g, '');
	return (negative ? '-' : '') + digits;
};

// Accepts both '.' and ',' as decimal separators (normalises before testing)
const isValidDecimal = (val: string | null): boolean => {
	if (val === null || val === '') return true;
	return /^-?\d+(\.\d+)?$/.test(String(val).replace(',', '.'));
};

// Returns a converterValue function that accepts the locale decimal separator.
// Stored values always arrive with '.'; user-typed values use decSep.
const makeDecimalConverter = () => (value: string | number | null) => {
	if (value === null || value === undefined || value === '') {
		return {float: null, full: '', error: false};
	}
	const str = String(value).trim();
	const normalized = str.replace(',', '.');
	if (/^-?\d+(\.\d+)?$/.test(normalized)) {
		return {float: parseFloat(normalized), full: str, error: false};
	}
	return {float: null, full: str, error: true};
};

// Returns a filterInput function that only allows the locale decimal separator.
const makeDecimalFilter =
	(decSep: string) =>
	(val: string): string => {
		const allowPattern = decSep === ',' ? /[^0-9,-]/g : /[^0-9.-]/g;
		const dupPattern = decSep === ',' ? /,/g : /\./g;
		let result = val.replace(allowPattern, '');
		const isNegative = result.startsWith('-');
		result = result.replace(/-/g, '');
		if (isNegative) result = '-' + result;
		const sepIdx = result.indexOf(decSep);
		if (sepIdx !== -1) {
			result = result.slice(0, sepIdx + 1) + result.slice(sepIdx + 1).replace(dupPattern, '');
		}
		return result;
	};

export function CustomFieldValueInput({
	customFieldKey,
	displayName,
	value,
	entityType,
	entityId,
	type,
	options = [],
	readOnly,
	language,
}: CustomFieldValueInputProps): React.JSX.Element {
	const decSep = profileLocales[language as keyof typeof profileLocales]?.usesCommaDecimal ? ',' : '.';

	// Convert stored server value ('.') to locale display format and ensure one decimal place.
	const toDisplay = (val: string | null) => {
		if (val === null || val === '') return val;
		const withSep = decSep === ',' ? val.replace('.', ',') : val;
		return withSep.includes(decSep) ? withSep : withSep + decSep + '0';
	};

	const [currentValue, setCurrentValue] = useState(toDisplay(value));

	useEffect(() => {
		setCurrentValue(toDisplay(value));
	}, [value, decSep]);

	const handleCustomFieldChange = (newValue: string | null) => {
		Util.CommitMutation(UpdateCustomFieldValueMutation, {
			__typename: GRAPHQL_TYPE_MAP[type],
			entityId,
			entityType,
			key: customFieldKey,
			value: newValue,
		});
	};

	const handleSave = () => {
		if (value !== currentValue) {
			if (type === CUSTOM_FIELD_TYPE.INTEGER && !isValidInteger(currentValue)) {
				return;
			}
			handleCustomFieldChange(currentValue);
		}
	};

	const onComboBoxChange = (optionId: string) => {
		setCurrentValue(optionId);
		handleCustomFieldChange(optionId);
	};

	if (type === CUSTOM_FIELD_TYPE.DROPDOWN) {
		return (
			<DesignSystemComboBoxBorderAdapter>
				<ComboBox
					data-cy={'custom-field-dropdown'}
					aria-label={displayName + ' dropdown field'}
					defaultItems={options}
					value={currentValue}
					onChange={(optionId: string) => onComboBoxChange(optionId)}
					width={ComboBox.WIDTH.FLEX}
					menuTrigger="focus"
					size={'small'}
				>
					{option => (
						<ComboBox.Item key={atob(option.id).split(':')[1]} textValue={option.displayName}>
							<Text data-cy={'option-' + option.displayName} ellipsis={true} tooltipOnEllipsis={true} size={'2'}>
								{option.displayName}
							</Text>
						</ComboBox.Item>
					)}
				</ComboBox>
			</DesignSystemComboBoxBorderAdapter>
		);
	}

	if (type === CUSTOM_FIELD_TYPE.INTEGER) {
		return (
			<BasicNumberInput
				value={currentValue ?? ''}
				converterValue={integerConverterValue}
				filterInput={filterIntegerInput}
				onChange={setCurrentValue}
				callback={(floatValue: number | null, e: unknown, isDirty: boolean) => {
					if (!isDirty) return;
					const inputValue = (e as React.FocusEvent<HTMLInputElement>).target.value;
					if (inputValue === '') {
						handleCustomFieldChange(null);
					} else if (floatValue != null && isValidInteger(String(floatValue))) {
						handleCustomFieldChange(String(floatValue));
					} else {
						setCurrentValue(value);
					}
				}}
				disabled={readOnly}
				cy={'custom-field-integer-input'}
			/>
		);
	}

	if (type === CUSTOM_FIELD_TYPE.DECIMAL) {
		return (
			<BasicNumberInput
				value={currentValue ?? ''}
				converterValue={makeDecimalConverter()}
				filterInput={makeDecimalFilter(decSep)}
				onChange={setCurrentValue}
				callback={(floatValue: number | null, e: unknown, isDirty: boolean) => {
					if (!isDirty) return;
					const inputValue = (e as React.FocusEvent<HTMLInputElement>).target.value;
					if (inputValue === '') {
						handleCustomFieldChange(null);
					} else if (floatValue != null && isValidDecimal(inputValue)) {
						handleCustomFieldChange(String(floatValue));
					} else {
						setCurrentValue(toDisplay(value));
					}
				}}
				disabled={readOnly}
				cy={'custom-field-decimal-input'}
			/>
		);
	}

	return <BasicTextInput value={currentValue ?? ''} onChange={setCurrentValue} callback={handleSave} disabled={readOnly} />;
}
